import React, { useState, useMemo, useEffect } from 'react';
import { VisitorLog, MemoryItem, CoupleSettings, WebAnalyticsData } from '../../types';
import {
  Activity,
  Users,
  Calendar,
  CalendarDays,
  CalendarRange,
  TrendingUp,
  Sparkles,
  Smartphone,
  Monitor,
  Tablet,
  Clock,
  Trash2,
  RefreshCw,
  ShieldCheck,
  CheckCircle2,
  ExternalLink,
  Info,
  Loader2,
} from 'lucide-react';
import { ConfirmationModal } from '../ConfirmationModal';
import {
  subscribeToAuthoritativeAnalytics,
  fetchAuthoritativeAnalytics,
} from '../../services/firebaseDb';

export interface WebStatsSectionProps {
  visitorLogs: VisitorLog[];
  memories?: MemoryItem[];
  settings?: CoupleSettings;
  onDeleteVisitorLog?: (id: string) => Promise<void> | void;
  onClearAllVisitorLogs?: () => Promise<void> | void;
  onBlockIp?: (
    ip: string,
    meta?: {
      reason?: string;
      deviceModel?: string;
      visitorId?: string;
      country?: string;
      city?: string;
      flagEmoji?: string;
    }
  ) => Promise<void> | void;
  onUnblockIp?: (ip: string, visitorId?: string) => Promise<void> | void;
  activeAdminProfile?: 'novio' | 'novia';
  onTriggerToast: (msg: string) => void;
}

export const VisitorRegistrySection: React.FC<WebStatsSectionProps> = ({
  visitorLogs = [],
  onDeleteVisitorLog,
  onClearAllVisitorLogs,
  onTriggerToast,
}) => {
  const [isClearingAll, setIsClearingAll] = useState(false);
  const [logToDelete, setLogToDelete] = useState<VisitorLog | null>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Persistent authoritative analytics state
  const [analytics, setAnalytics] = useState<WebAnalyticsData>(() => {
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem('nuestros_recuerdos_stats_cache');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && typeof parsed.totalHistoricalVisits === 'number') {
            return parsed;
          }
        }
      } catch {}
    }
    return {
      activeUsersCount: 0,
      todayVisits: 0,
      weekVisits: 0,
      monthVisits: 0,
      yearVisits: 0,
      totalHistoricalVisits: 0,
      mobileCount: 0,
      desktopCount: 0,
      tabletCount: 0,
      mobilePercent: 0,
      desktopPercent: 0,
      tabletPercent: 0,
      dailyBuckets: [],
      maxDayCount: 1,
      recentLogs: [],
      status: 'loading',
    };
  });

  // Real-time authoritative analytics subscription (backend + Firestore presence)
  useEffect(() => {
    const unsub = subscribeToAuthoritativeAnalytics((fresh) => {
      setAnalytics((prev) => {
        // Monotonic guard: never overwrite valid historical numbers with an unconfirmed empty zero
        if (
          prev.totalHistoricalVisits > 0 &&
          fresh.totalHistoricalVisits === 0 &&
          fresh.status !== 'available'
        ) {
          return { ...prev, status: 'updating' };
        }
        return fresh;
      });
    });
    return () => unsub();
  }, []);

  // Update current time every 10 seconds to keep live metrics up to date
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  // Merge Firestore detailed logs and server recent session logs
  const displayLogs = useMemo(() => {
    if (Array.isArray(visitorLogs) && visitorLogs.length > 0) {
      return visitorLogs;
    }
    return analytics.recentLogs || [];
  }, [visitorLogs, analytics.recentLogs]);

  // Compute calculated metrics directly from persistent analytics and real-time sessions
  const stats = useMemo(() => {
    const now = currentTime;
    const nowMs = now.getTime();

    // 1. Live active visitors (heartbeat strictly within the last 90 seconds)
    // Never count historical logs with stale isOnline: true without a fresh heartbeat
    let activeFromLogs = 0;
    for (const log of displayLogs) {
      const lastActiveMs = log.lastActiveAt ? new Date(log.lastActiveAt).getTime() : 0;
      const lastVisitedMs = log.lastVisitedAt ? new Date(log.lastVisitedAt).getTime() : 0;
      const diffSec = (nowMs - Math.max(lastActiveMs, lastVisitedMs)) / 1000;
      if (diffSec >= 0 && diffSec <= 90) {
        activeFromLogs++;
      }
    }

    const activeUsersCount = Math.max(analytics.activeUsersCount, activeFromLogs);

    // 2. Authoritative real metrics directly from persistent database/store
    const todayVisits = analytics.todayVisits;
    const weekVisits = analytics.weekVisits;
    const monthVisits = analytics.monthVisits;
    const yearVisits = analytics.yearVisits;
    const totalHistoricalVisits = analytics.totalHistoricalVisits;

    // 3. Device counts and percentages
    const mobileCount = analytics.mobileCount || 0;
    const desktopCount = analytics.desktopCount || 0;
    const tabletCount = analytics.tabletCount || 0;
    const totalDevices = mobileCount + desktopCount + tabletCount || 1;

    let mobilePercent = analytics.mobilePercent;
    let desktopPercent = analytics.desktopPercent;
    let tabletPercent = analytics.tabletPercent;

    if (totalDevices > 0 && !mobilePercent && !desktopPercent && !tabletPercent) {
      mobilePercent = Math.round((mobileCount / totalDevices) * 100);
      desktopPercent = Math.round((desktopCount / totalDevices) * 100);
      tabletPercent = Math.max(0, 100 - mobilePercent - desktopPercent);
    }

    // 4. Daily buckets for the last 7 days chart in America/Bogota
    let dailyBuckets = analytics.dailyBuckets;
    if (!dailyBuckets || dailyBuckets.length === 0) {
      dailyBuckets = [];
      const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
        const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const label = i === 0 ? 'Hoy' : i === 1 ? 'Ayer' : dayNames[d.getDay()];
        dailyBuckets.push({ dateStr: dateKey, label, count: i === 0 ? todayVisits : 0 });
      }
    }

    const maxDayCount = Math.max(1, ...dailyBuckets.map((b) => b.count));

    return {
      activeUsersCount,
      todayVisits,
      weekVisits,
      monthVisits,
      yearVisits,
      totalHistoricalVisits,
      mobileCount,
      desktopCount,
      tabletCount,
      mobilePercent,
      desktopPercent,
      tabletPercent,
      dailyBuckets,
      maxDayCount,
    };
  }, [analytics, displayLogs, currentTime]);

  const renderCardMetric = (value: number) => {
    if (analytics.status === 'loading') {
      return (
        <div className="flex items-center gap-2 text-slate-400 py-1.5">
          <Loader2 className="w-5 h-5 animate-spin text-teal-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cargando...</span>
        </div>
      );
    }
    if (analytics.status === 'error' && stats.totalHistoricalVisits === 0) {
      return (
        <div className="flex items-center gap-1.5 text-amber-400 py-1.5">
          <Info className="w-4 h-4 shrink-0" />
          <span className="text-sm font-medium">No disponible</span>
        </div>
      );
    }
    return (
      <div className="flex items-baseline gap-2">
        <span className="text-4xl sm:text-5xl font-black text-white tracking-tight">
          {value}
        </span>
        {analytics.status === 'updating' && (
          <span
            className="w-2 h-2 rounded-full bg-blue-400 animate-pulse inline-block"
            title="Actualizando lectura en segundo plano..."
          />
        )}
      </div>
    );
  };

  // Format relative time helper
  const formatTimeAgo = (dateStr?: string) => {
    if (!dateStr) return 'Reciente';
    try {
      const past = new Date(dateStr).getTime();
      const diffSec = Math.floor((Date.now() - past) / 1000);
      if (diffSec < 45) return 'En este momento';
      if (diffSec < 90) return 'Hace 1 minuto';
      if (diffSec < 3600) return `Hace ${Math.floor(diffSec / 60)} min`;
      if (diffSec < 86400) return `Hace ${Math.floor(diffSec / 3600)} h`;
      const days = Math.floor(diffSec / 86400);
      if (days === 1) return 'Ayer';
      if (days < 7) return `Hace ${days} días`;
      return new Date(dateStr).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
      });
    } catch {
      return 'Reciente';
    }
  };

  // Month name helper
  const currentMonthName = useMemo(() => {
    return currentTime.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  }, [currentTime]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    setCurrentTime(new Date());
    try {
      const fresh = await fetchAuthoritativeAnalytics();
      setAnalytics((prev) => {
        if (
          prev.totalHistoricalVisits > 0 &&
          fresh.totalHistoricalVisits === 0 &&
          fresh.status !== 'available'
        ) {
          return prev;
        }
        return fresh;
      });
      onTriggerToast('Estadísticas sincronizadas con el servidor');
    } catch {
      onTriggerToast('Error al actualizar estadísticas');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!logToDelete || !onDeleteVisitorLog) return;
    try {
      await onDeleteVisitorLog(logToDelete.id);
      onTriggerToast('Registro eliminado correctamente');
    } catch {
      onTriggerToast('Error al eliminar el registro');
    } finally {
      setLogToDelete(null);
    }
  };

  const handleConfirmClearAll = async () => {
    if (!onClearAllVisitorLogs) return;
    try {
      await onClearAllVisitorLogs();
      const emptyAnalytics: WebAnalyticsData = {
        activeUsersCount: 0,
        todayVisits: 0,
        weekVisits: 0,
        monthVisits: 0,
        yearVisits: 0,
        totalHistoricalVisits: 0,
        mobileCount: 0,
        desktopCount: 0,
        tabletCount: 0,
        mobilePercent: 0,
        desktopPercent: 0,
        tabletPercent: 0,
        dailyBuckets: [],
        maxDayCount: 1,
        recentLogs: [],
        status: 'available',
      };
      setAnalytics(emptyAnalytics);
      try {
        localStorage.removeItem('nuestros_recuerdos_stats_cache');
      } catch {}
      onTriggerToast('Todas las estadísticas han sido reiniciadas');
    } catch {
      onTriggerToast('Error al reiniciar estadísticas');
    } finally {
      setIsClearingAll(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* HEADER PRINCIPAL DEL MÓDULO */}
      <div className="bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-8 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="px-3 py-1 bg-teal-500/20 text-teal-300 text-xs font-bold rounded-full border border-teal-500/30 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-teal-400 animate-ping inline-block" />
                <span>Tiempo real</span>
              </span>
              <span className="px-3 py-1 bg-white/5 text-slate-300 text-xs font-medium rounded-full border border-white/10 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>100% Privado y Ético</span>
              </span>
              {analytics.status === 'updating' && (
                <span className="px-2.5 py-0.5 bg-blue-500/10 text-blue-300 text-[11px] font-medium rounded-full border border-blue-500/20">
                  Sincronizando...
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3">
              <span>Estadísticas de la web</span>
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">
              Consulta el tráfico e impacto en tiempo real de tu página web. Monitoreo limpio y
              respetuoso que protege la privacidad de cada persona.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start md:self-auto flex-wrap">
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 rounded-xl text-xs font-semibold transition-all cursor-pointer active:scale-95 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-teal-400 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Actualizar</span>
            </button>

            {onClearAllVisitorLogs && (displayLogs.length > 0 || stats.totalHistoricalVisits > 0) && (
              <button
                type="button"
                onClick={() => setIsClearingAll(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 rounded-xl text-xs font-semibold transition-all cursor-pointer active:scale-95"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                <span>Reiniciar métricas</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* LAS 6 PREGUNTAS Y MÉTRICAS FUNDAMENTALES (GRID 3x2 / 2x3) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {/* 1. USUARIOS ACTIVOS AHORA */}
        <div className="bg-gradient-to-br from-emerald-950/40 via-slate-900/80 to-slate-900/90 border border-emerald-500/30 rounded-3xl p-6 relative overflow-hidden shadow-xl hover:border-emerald-500/50 transition-all group">
          <div className="absolute top-0 right-0 w-28 h-28 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-300 flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span>Activos en tiempo real</span>
            </span>
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <Activity className="w-5 h-5" />
            </div>
          </div>
          <div className="space-y-1">
            {renderCardMetric(stats.activeUsersCount)}
            <p className="text-xs font-medium text-slate-400 flex items-center gap-1.5 pt-1">
              <span>{stats.activeUsersCount === 1 ? 'Persona visitando la web ahora' : 'Personas visitando la web ahora'}</span>
            </p>
          </div>
        </div>

        {/* 2. VISITAS HOY */}
        <div className="bg-gradient-to-br from-blue-950/30 via-slate-900/80 to-slate-900/90 border border-blue-500/20 rounded-3xl p-6 relative overflow-hidden shadow-xl hover:border-blue-500/40 transition-all group">
          <div className="absolute top-0 right-0 w-28 h-28 bg-blue-500/10 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-300 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />
              <span>Visitas hoy</span>
            </span>
            <div className="w-10 h-10 rounded-2xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
              <CalendarDays className="w-5 h-5" />
            </div>
          </div>
          <div className="space-y-1">
            {renderCardMetric(stats.todayVisits)}
            <p className="text-xs font-medium text-slate-400 pt-1">
              <span>Tráfico registrado el día de hoy (desde 00:00 hrs)</span>
            </p>
          </div>
        </div>

        {/* 3. VISITAS ESTA SEMANA */}
        <div className="bg-gradient-to-br from-indigo-950/30 via-slate-900/80 to-slate-900/90 border border-indigo-500/20 rounded-3xl p-6 relative overflow-hidden shadow-xl hover:border-indigo-500/40 transition-all group">
          <div className="absolute top-0 right-0 w-28 h-28 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-300 flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>Visitas esta semana</span>
            </span>
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
              <CalendarRange className="w-5 h-5" />
            </div>
          </div>
          <div className="space-y-1">
            {renderCardMetric(stats.weekVisits)}
            <p className="text-xs font-medium text-slate-400 pt-1">
              <span>Tráfico en el transcurso de los últimos 7 días</span>
            </p>
          </div>
        </div>

        {/* 4. VISITAS ESTE MES */}
        <div className="bg-gradient-to-br from-pink-950/30 via-slate-900/80 to-slate-900/90 border border-pink-500/20 rounded-3xl p-6 relative overflow-hidden shadow-xl hover:border-pink-500/40 transition-all group">
          <div className="absolute top-0 right-0 w-28 h-28 bg-pink-500/10 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-pink-300 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Visitas este mes</span>
            </span>
            <div className="w-10 h-10 rounded-2xl bg-pink-500/20 border border-pink-500/30 flex items-center justify-center text-pink-400">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="space-y-1">
            {renderCardMetric(stats.monthVisits)}
            <p className="text-xs font-medium text-slate-400 pt-1 capitalize">
              <span>{currentMonthName}</span>
            </p>
          </div>
        </div>

        {/* 5. VISITAS ESTE AÑO */}
        <div className="bg-gradient-to-br from-amber-950/30 via-slate-900/80 to-slate-900/90 border border-amber-500/20 rounded-3xl p-6 relative overflow-hidden shadow-xl hover:border-amber-500/40 transition-all group">
          <div className="absolute top-0 right-0 w-28 h-28 bg-amber-500/10 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-300 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />
              <span>Visitas este año</span>
            </span>
            <div className="w-10 h-10 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
          <div className="space-y-1">
            {renderCardMetric(stats.yearVisits)}
            <p className="text-xs font-medium text-slate-400 pt-1">
              <span>Año en curso ({currentTime.getFullYear()})</span>
            </p>
          </div>
        </div>

        {/* 6. TOTAL DESDE QUE COMENZÓ EL REGISTRO */}
        <div className="bg-gradient-to-br from-rose-950/40 via-slate-900/80 to-slate-900/90 border border-rose-500/30 rounded-3xl p-6 relative overflow-hidden shadow-xl hover:border-rose-500/50 transition-all group">
          <div className="absolute top-0 right-0 w-28 h-28 bg-rose-500/10 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-rose-300 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Total histórico</span>
            </span>
            <div className="w-10 h-10 rounded-2xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center text-rose-400">
              <Activity className="w-5 h-5" />
            </div>
          </div>
          <div className="space-y-1">
            {renderCardMetric(stats.totalHistoricalVisits)}
            <p className="text-xs font-medium text-slate-400 pt-1">
              <span>Visitas acumuladas desde el inicio del registro</span>
            </p>
          </div>
        </div>
      </div>

      {/* GRÁFICO VISUAL: TENDENCIA DE LOS ÚLTIMOS 7 DÍAS & DISPOSITIVOS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* GRÁFICO DE BARRAS DE LOS ÚLTIMOS 7 DÍAS (2 COLUMNAS) */}
        <div className="lg:col-span-2 bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-8 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-teal-400" />
                <span>Tendencia de los últimos 7 días</span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Distribución de visitas por día
              </p>
            </div>
            <span className="px-3 py-1 bg-white/5 text-slate-300 text-xs font-medium rounded-full border border-white/10">
              {stats.weekVisits} visitas esta semana
            </span>
          </div>

          <div className="pt-4 pb-2">
            <div className="grid grid-cols-7 gap-2 sm:gap-4 items-end h-44 sm:h-52">
              {stats.dailyBuckets.map((bucket, index) => {
                const heightPercent = stats.maxDayCount > 0 ? Math.max(12, Math.round((bucket.count / stats.maxDayCount) * 100)) : 12;
                const isToday = index === stats.dailyBuckets.length - 1;

                return (
                  <div key={bucket.dateStr} className="flex flex-col items-center h-full justify-end group">
                    <span className="text-[10px] sm:text-xs font-bold text-slate-400 group-hover:text-white mb-2 transition-colors">
                      {bucket.count}
                    </span>
                    <div className="w-full max-w-[42px] bg-slate-800/80 rounded-2xl overflow-hidden flex flex-col justify-end p-1 h-32 sm:h-36 border border-white/5 group-hover:border-teal-500/40 transition-all">
                      <div
                        style={{ height: `${heightPercent}%` }}
                        className={`w-full rounded-xl transition-all duration-500 ${
                          isToday
                            ? 'bg-gradient-to-t from-teal-500 to-emerald-400 shadow-lg shadow-teal-500/20'
                            : 'bg-gradient-to-t from-slate-700 to-slate-500 group-hover:from-teal-600 group-hover:to-teal-400'
                        }`}
                      />
                    </div>
                    <span className={`text-[11px] sm:text-xs font-semibold mt-3 ${isToday ? 'text-teal-400 font-bold' : 'text-slate-400'}`}>
                      {bucket.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* DISTRIBUCIÓN POR TIPO DE DISPOSITIVO (1 COLUMNA) */}
        <div className="bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-col justify-between">
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2 mb-1">
              <Smartphone className="w-4 h-4 text-pink-400" />
              <span>Dispositivos</span>
            </h2>
            <p className="text-xs text-slate-400 mb-6">
              Categoría de navegación
            </p>

            <div className="space-y-4">
              {/* MÓVIL */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-slate-200 font-medium">
                    <Smartphone className="w-3.5 h-3.5 text-pink-400" />
                    <span>Móvil / Celular</span>
                  </span>
                  <span className="font-bold text-white">
                    {stats.mobilePercent}% <span className="text-slate-400 font-normal">({stats.mobileCount})</span>
                  </span>
                </div>
                <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    style={{ width: `${stats.mobilePercent}%` }}
                    className="h-full bg-gradient-to-r from-pink-500 to-rose-400 rounded-full transition-all duration-500"
                  />
                </div>
              </div>

              {/* ESCRITORIO */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-slate-200 font-medium">
                    <Monitor className="w-3.5 h-3.5 text-blue-400" />
                    <span>Computador / PC</span>
                  </span>
                  <span className="font-bold text-white">
                    {stats.desktopPercent}% <span className="text-slate-400 font-normal">({stats.desktopCount})</span>
                  </span>
                </div>
                <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    style={{ width: `${stats.desktopPercent}%` }}
                    className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-500"
                  />
                </div>
              </div>

              {/* TABLET */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-slate-200 font-medium">
                    <Tablet className="w-3.5 h-3.5 text-amber-400" />
                    <span>Tablet</span>
                  </span>
                  <span className="font-bold text-white">
                    {stats.tabletPercent}% <span className="text-slate-400 font-normal">({stats.tabletCount})</span>
                  </span>
                </div>
                <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    style={{ width: `${stats.tabletPercent}%` }}
                    className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full transition-all duration-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-5 border-t border-white/5 text-[11px] text-slate-400 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span>Datos agregados respetando los estándares de privacidad.</span>
          </div>
        </div>
      </div>

      {/* REGISTRO RECIENTE DE ACTIVIDAD: SIMPLE, LIMPIO Y NO INVASIVO */}
      <div className="bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-8 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-teal-400" />
              <span>Registro reciente de visitas</span>
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Últimas sesiones conectadas al sitio (actualizado en tiempo real)
            </p>
          </div>
          <span className="text-xs text-slate-400 self-start sm:self-auto">
            Total sesiones registradas: <strong className="text-white">{displayLogs.length}</strong>
          </span>
        </div>

        {displayLogs.length === 0 ? (
          <div className="text-center py-12 px-4 border border-dashed border-white/10 rounded-2xl bg-white/5">
            <Activity className="w-8 h-8 text-teal-400/50 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-300">Aún no hay visitas registradas</p>
            <p className="text-xs text-slate-500 mt-1">
              Cuando alguien ingrese a tu web, las visitas aparecerán aquí automáticamente en tiempo real.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6 sm:mx-0">
            <div className="inline-block min-w-full align-middle px-6 sm:px-0">
              <div className="border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/5">
                {displayLogs.slice(0, 15).map((log, idx) => {
                  const lastActiveMs = log.lastActiveAt ? new Date(log.lastActiveAt).getTime() : 0;
                  const lastVisitedMs = log.lastVisitedAt ? new Date(log.lastVisitedAt).getTime() : 0;
                  const diffSeconds = (Date.now() - Math.max(lastActiveMs, lastVisitedMs)) / 1000;
                  const isOnline = diffSeconds < 60 || log.isOnline;

                  const DeviceIcon = log.deviceType === 'Móvil' ? Smartphone : log.deviceType === 'Tablet' ? Tablet : Monitor;

                  return (
                    <div
                      key={log.id || `log-${idx}`}
                      className="flex items-center justify-between p-4 hover:bg-white/[0.03] transition-colors gap-4"
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center border shrink-0 ${
                          isOnline
                            ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                            : 'bg-white/5 border-white/10 text-slate-400'
                        }`}>
                          <DeviceIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-white">
                              {log.deviceType || 'Dispositivo'}
                            </span>
                            {isOnline ? (
                              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] font-bold rounded-full border border-emerald-500/30 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                <span>En línea</span>
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-400">
                                {formatTimeAgo(log.lastActiveAt || log.lastVisitedAt)}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 truncate mt-0.5">
                            Visitas acumuladas de esta sesión: <strong className="text-slate-300">{log.visitCount || 1}</strong>
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-[11px] text-slate-500 hidden sm:inline">
                          {log.lastVisitedAt ? new Date(log.lastVisitedAt).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          }) : ''}
                        </span>

                        {onDeleteVisitorLog && (
                          <button
                            type="button"
                            onClick={() => setLogToDelete(log)}
                            title="Eliminar este registro"
                            className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AVISO DE PRIVACIDAD GARANTIZADA */}
      <div className="bg-slate-900/60 border border-emerald-500/20 rounded-2xl p-5 flex items-start sm:items-center gap-4 text-xs text-slate-300 shadow-md">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="space-y-0.5">
          <p className="font-bold text-white text-xs sm:text-sm">
            Privacidad 100% Protegida en Nuestros Recuerdos
          </p>
          <p className="text-slate-400 text-xs">
            Este módulo no utiliza GPS, no rastrea ubicaciones geográficas, no almacena coordenadas, ni recopila datos invasivos. Su única función es contabilizar con precisión las visitas y los usuarios activos de tu web.
          </p>
        </div>
      </div>

      {/* MODAL DE CONFIRMACIÓN PARA ELIMINAR UN REGISTRO */}
      {logToDelete && (
        <ConfirmationModal
          isOpen={true}
          title="Eliminar registro"
          message="¿Estás seguro de que deseas eliminar este registro de visita?"
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
          onConfirm={handleConfirmDelete}
          onCancel={() => setLogToDelete(null)}
        />
      )}

      {/* MODAL DE CONFIRMACIÓN PARA REINICIAR TODAS LAS ESTADÍSTICAS */}
      {isClearingAll && (
        <ConfirmationModal
          isOpen={true}
          title="Reiniciar estadísticas"
          message="¿Deseas reiniciar todas las estadísticas acumuladas? Esta acción limpiará los registros de visitas en Firebase y comenzará el conteo desde cero."
          confirmText="Sí, reiniciar todo"
          cancelText="Cancelar"
          type="danger"
          onConfirm={handleConfirmClearAll}
          onCancel={() => setIsClearingAll(false)}
        />
      )}
    </div>
  );
};

export const WebStatsSection = VisitorRegistrySection;

